import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import BarcodeScanner from '../../components/shared/BarcodeScanner';
import { ClipboardCheck, ScanLine, CheckCircle2, CloudOff, AlertTriangle } from 'lucide-react';
import stocktakeService, {
  recordCount, flushStocktakeQueue, queuedCounts, subscribeStocktakeQueue, dropQueuedCount, type QueuedCount,
} from '../../services/nurseryStocktake';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useOrgTimezone, formatDateTimeInTz } from '../../utils/timezone';
import { errorMessage } from '../../utils/apiResponse';
import { parseLotScan, scanMatches } from '../../utils/lotScan';
import { compareBlocks, compareSlots, rowLabel } from '../../utils/nurseryRowSlots';
import { cn } from '../../utils/cn';
import type { StocktakeDetail, StocktakeLot } from '../../types/nurseryStocktake';

/**
 * /nursery/stocktake/:id — walk the rows in order and count each lot.
 *
 * One tap when the record is right, the number when it is not. A count with
 * no signal waits on the phone and goes when signal returns; the server
 * applies it as a difference from what this page showed, so a pick made in
 * between is not undone.
 */

const signed = (n: number) => (n > 0 ? `+${n}` : String(n));

function treeName(l: StocktakeLot) {
  return [l.cultivar_name, l.rootstock_name].filter(Boolean).join(' / ');
}

function placeName(l: StocktakeLot) {
  return l.sequence_number != null ? `Position ${l.sequence_number}` : (l.location_name || 'No position');
}

function sectionTitle(l: StocktakeLot) {
  const where = l.row_number != null ? rowLabel(l.row_number, l.row_side) : l.location_name;
  return [l.block_name, where].filter(Boolean).join(' · ') || 'Elsewhere';
}

const sectionKey = (l: StocktakeLot) =>
  l.row_number != null ? `${l.block_id}:${l.row_number}:${l.row_side ?? ''}` : `${l.block_id}:loc:${l.location_id}`;

function walkOrder(a: StocktakeLot, b: StocktakeLot) {
  return compareBlocks({ name: a.block_name, sort_order: a.block_sort_order }, { name: b.block_name, sort_order: b.block_sort_order })
    || compareSlots(a, b)
    || a.lot_id - b.lot_id;
}

interface CardProps {
  lot: StocktakeLot;
  queued: QueuedCount | undefined;
  editable: boolean;
  highlighted: boolean;
  onCount: (lot: StocktakeLot, counted: number) => Promise<void>;
}

function LotCountCard({ lot, queued, editable, highlighted, onCount }: CardProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pendingCount = queued && !queued.refused ? queued.payload : null;
  const counted = pendingCount
    ? { counted: pendingCount.counted, shown: pendingCount.shown }
    : lot.count ? { counted: lot.count.counted_count, shown: lot.count.shown_count } : null;
  const showInput = editable && (editing || !counted);

  const save = async (n: number) => {
    setSaving(true);
    setError(null);
    try {
      await onCount(lot, n);
      setEditing(false);
      setValue('');
    } catch (e) {
      setError(errorMessage(e, 'Could not save the count'));
    } finally {
      setSaving(false);
    }
  };

  const typed = value.trim() === '' ? null : Number(value);
  const typedOk = typed != null && Number.isInteger(typed) && typed >= 0;

  return (
    <li
      id={`lot-${lot.lot_id}`}
      className={cn(
        'bg-white border rounded-xl p-4',
        highlighted ? 'border-primary-500 ring-2 ring-primary-200' : counted ? 'border-green-200' : 'border-neutral-200',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium text-neutral-500">{placeName(lot)}</p>
          <p className="font-semibold text-neutral-900 break-words">{treeName(lot)}</p>
          <p className="text-xs text-neutral-500 break-words">
            {lot.tree_type_name}{lot.lot_code ? ` · ${lot.lot_code}` : ` · Lot #${lot.lot_id}`}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-xs text-neutral-500">On record</p>
          <p className="text-lg font-semibold text-neutral-900">{lot.current_count.toLocaleString()}</p>
        </div>
      </div>

      {counted && !editing && (
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
          {pendingCount ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-amber-800 min-w-0">
              <CloudOff className="h-4 w-4 flex-shrink-0" />
              <span className="break-words">Counted {pendingCount.counted}, waiting for signal</span>
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-sm text-green-800 min-w-0">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
              <span className="break-words">
                Counted {counted.counted}
                {counted.counted !== counted.shown && ` (${signed(counted.counted - counted.shown)})`}
                {lot.count?.counted_by_name ? ` by ${lot.count.counted_by_name}` : ''}
              </span>
            </span>
          )}
          {editable && (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>Count again</Button>
          )}
        </div>
      )}

      {queued?.refused && (
        <div className="mt-3 bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 space-y-1">
          <p className="break-words">A count of {queued.payload.counted} was not saved: {queued.refused}</p>
          <button type="button" className="underline text-xs" onClick={() => dropQueuedCount(queued.payload.client_uuid)}>Dismiss</button>
        </div>
      )}

      {showInput && (
        <div className="mt-3 space-y-2">
          <div className="flex flex-wrap items-end gap-2">
            <Button onClick={() => save(lot.current_count)} disabled={saving} className="max-w-full">
              <span className="truncate">{lot.current_count.toLocaleString()} is right</span>
            </Button>
            <span className="text-sm text-neutral-500 pb-2">or</span>
            <label className="flex items-end gap-2 min-w-0">
              <span className="sr-only">Trees counted</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="Counted"
                aria-label={`Trees counted at ${placeName(lot)}`}
                className="w-24 rounded-lg border border-neutral-300 px-3 py-2 text-sm"
                value={value}
                onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter' && typedOk) save(typed); }}
              />
            </label>
            <Button variant="outline" onClick={() => typedOk && save(typed)} disabled={!typedOk || saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {editing && (
              <Button variant="ghost" onClick={() => { setEditing(false); setValue(''); }}>Cancel</Button>
            )}
          </div>
          {typedOk && typed !== lot.current_count && (
            <p className="text-xs text-neutral-600">
              {signed(typed - lot.current_count)} against the record. The lot will be set to {typed}.
            </p>
          )}
          {error && <p className="text-sm text-red-700 break-words">{error}</p>}
        </div>
      )}
    </li>
  );
}

function StocktakeCount() {
  const { id: idParam } = useParams();
  const id = Number(idParam);
  const tz = useOrgTimezone();
  const { hasAccess, loading: licenseLoading } = useModuleLicense('nursery', ['picker', 'operator', 'manager', 'admin']);
  const [detail, setDetail] = useState<StocktakeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [queue, setQueue] = useState(() => queuedCounts(id));
  const [onlyToCount, setOnlyToCount] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanNote, setScanNote] = useState<string | null>(null);
  const [found, setFound] = useState<number | null>(null);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const [finishing, setFinishing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await stocktakeService.get(id);
      if (res.success) setDetail(res.data);
      else setError(errorMessage(res, 'Could not load the stocktake'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load the stocktake'));
    }
  }, [id]);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);
  useEffect(() => subscribeStocktakeQueue(() => setQueue(queuedCounts(id))), [id]);

  // Send anything waiting: on opening, when signal returns, and every 30 s
  // while something is waiting. A flush that sent something reloads, since
  // the lots it counted have moved.
  const waiting = [...queue.values()].filter((q) => !q.refused).length;
  useEffect(() => {
    if (!hasAccess) return;
    const flush = () => flushStocktakeQueue().then((r) => { if (r.sent) load(); }).catch(() => { /* tried again later */ });
    flush();
    window.addEventListener('online', flush);
    const timer = waiting ? window.setInterval(flush, 30000) : undefined;
    return () => { window.removeEventListener('online', flush); if (timer) window.clearInterval(timer); };
  }, [hasAccess, waiting, load]);

  const lots = useMemo(() => [...(detail?.lots ?? [])].sort(walkOrder), [detail]);
  const isCounted = useCallback((l: StocktakeLot) => !!l.count || !!(queue.get(l.lot_id) && !queue.get(l.lot_id)?.refused), [queue]);
  const countedNow = lots.filter(isCounted).length;
  const sections = useMemo(() => {
    const out: { key: string; title: string; lots: StocktakeLot[] }[] = [];
    for (const l of lots) {
      if (onlyToCount && isCounted(l) && found !== l.lot_id) continue;
      const key = sectionKey(l);
      const last = out[out.length - 1];
      if (last?.key === key) last.lots.push(l);
      else out.push({ key, title: sectionTitle(l), lots: [l] });
    }
    return out;
  }, [lots, onlyToCount, isCounted, found]);

  const onCount = async (lot: StocktakeLot, counted: number) => {
    const record = await recordCount(id, { lot_id: lot.lot_id, shown: lot.current_count, counted });
    if (!record) return; // queued; the card reads it from the queue
    setDetail((d) => d && ({
      ...d,
      lots: d.lots.map((l) => l.lot_id !== lot.lot_id ? l : {
        ...l,
        current_count: record.count_after,
        count: {
          id: record.id, shown_count: record.shown_count, counted_count: record.counted_count,
          count_before: record.count_before, count_after: record.count_after, note: record.note,
          counted_at: record.counted_at, counted_by_name: null,
        },
      }),
    }));
    if (found === lot.lot_id) setFound(null);
  };

  const onScan = (raw: string) => {
    setScanning(false);
    const scan = parseLotScan(raw);
    if (!scan) return;
    const name = 'lotId' in scan ? `Lot #${scan.lotId}` : scan.code;
    const lot = lots.find((l) => scanMatches(scan, { id: l.lot_id, code: l.lot_code }));
    if (!lot) {
      setFound(null);
      setScanNote(`${name} is not at ${detail?.stocktake.block_name || detail?.stocktake.farm_name}, so it is not part of this count.`);
      return;
    }
    setFound(lot.lot_id);
    setScanNote(`${lot.lot_code || name}: ${sectionTitle(lot)}, ${placeName(lot).toLowerCase()}.`);
    requestAnimationFrame(() => document.getElementById(`lot-${lot.lot_id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  const finish = async () => {
    setFinishing(true);
    try {
      const res = await stocktakeService.finish(id);
      if (!res.success) throw new Error(errorMessage(res, 'Could not finish the count'));
      setDetail(res.data);
      setConfirmFinish(false);
    } catch (e) {
      setError(errorMessage(e, 'Could not finish the count'));
    } finally {
      setFinishing(false);
    }
  };

  if (licenseLoading || (!detail && !error)) {
    return <div className="max-w-2xl mx-auto px-4 py-6"><div className="h-40 bg-neutral-100 rounded-xl animate-pulse" /></div>;
  }
  if (!hasAccess || !detail) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8 text-center">
        <p className="text-sm text-red-700 mb-4 break-words">{error || 'Counting stock needs a nursery seat.'}</p>
        <Link to="/nursery/stocktake" className="text-sm text-primary-700 underline">Back to stocktake</Link>
      </div>
    );
  }

  const { stocktake: st, summary } = detail;
  const open = st.status === 'open';
  const where = st.block_name ? `${st.farm_name} · ${st.block_name}` : `${st.farm_name} · every block`;
  const total = open ? lots.length : summary.lots_total;
  const done = open ? countedNow : summary.lots_counted;
  const pct = total ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const differing = lots.filter((l) => l.count && l.count.counted_count !== l.count.shown_count);
  // Counts saved since the page loaded are only in `lots`, so an open count
  // totals from there rather than from the summary it was loaded with.
  const difference = open
    ? lots.reduce((sum, l) => sum + (l.count ? l.count.counted_count - l.count.shown_count : 0), 0)
    : summary.difference;
  const anyCounted = open ? lots.some((l) => l.count) : summary.lots_counted > 0;

  return (
    <>
      <PageHeader title={`Stocktake ${st.label}`} subtitle={where} icon={<ClipboardCheck className="h-6 w-6 text-white" />} />
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>}

        <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-2" aria-label="Progress">
          <div className="h-2 rounded-full bg-neutral-100 overflow-hidden" aria-label={`${done} of ${total} lots counted`}>
            <div className="h-full bg-green-500" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-sm text-neutral-700">
            {done} of {total} lots counted
            {anyCounted && ` · ${difference === 0 ? (open ? 'no difference so far' : 'no difference') : `${signed(difference)} trees against the record`}`}
          </p>
          {waiting > 0 && (
            <p className="flex items-start gap-1.5 text-sm text-amber-800">
              <CloudOff className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span className="break-words min-w-0">
                {waiting} count{waiting === 1 ? ' is' : 's are'} waiting for signal on this phone. Keep the page open until they go.
              </span>
            </p>
          )}
          {!open && (
            <p className="text-sm text-neutral-600 break-words">
              Finished {st.finished_at ? formatDateTimeInTz(st.finished_at, tz) : ''}{st.finished_by_name ? ` by ${st.finished_by_name}` : ''}.
              {summary.lots_total > summary.lots_counted && ` ${summary.lots_total - summary.lots_counted} lots were not counted.`}
            </p>
          )}
        </section>

        {open ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { setScanNote(null); setScanning(true); }}>
                <ScanLine className="h-4 w-4" /> Scan a lot label
              </Button>
              <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
                <input type="checkbox" checked={onlyToCount} onChange={(e) => setOnlyToCount(e.target.checked)} />
                Only lots still to count
              </label>
              {scanNote && <span className="text-sm text-neutral-700 break-words min-w-0 basis-full">{scanNote}</span>}
            </div>

            {lots.length === 0 && (
              <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center text-sm text-neutral-500">
                No lots are placed here.
              </div>
            )}
            {lots.length > 0 && sections.length === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-sm text-green-800 flex items-start gap-2">
                <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
                <span>Every lot here has been counted.</span>
              </div>
            )}

            {sections.map((s) => (
              <section key={s.key} aria-label={s.title}>
                <h2 className="text-sm font-semibold text-neutral-700 break-words">{s.title}</h2>
                <ul className="mt-2 space-y-3">
                  {s.lots.map((l) => (
                    <LotCountCard
                      key={l.lot_id}
                      lot={l}
                      queued={queue.get(l.lot_id)}
                      editable
                      highlighted={found === l.lot_id}
                      onCount={onCount}
                    />
                  ))}
                </ul>
              </section>
            ))}

            <section className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3" aria-label="Finish">
              {!confirmFinish ? (
                <Button variant="outline" onClick={() => setConfirmFinish(true)}>Finish this count</Button>
              ) : (
                <>
                  <p className="text-sm text-neutral-800 break-words">
                    {total - countedNow > 0
                      ? `${total - countedNow} of ${total} lots have not been counted. Their records stay as they are.`
                      : 'Every lot has been counted.'}
                    {waiting > 0 && ` ${waiting} count${waiting === 1 ? ' is' : 's are'} still waiting for signal and will be refused once it is finished.`}
                    {' '}Nobody can add counts to it afterwards.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={finish} disabled={finishing}>{finishing ? 'Finishing…' : 'Finish'}</Button>
                    <Button variant="ghost" onClick={() => setConfirmFinish(false)}>Keep counting</Button>
                  </div>
                </>
              )}
            </section>
          </>
        ) : (
          <section aria-label="Differences">
            <h2 className="text-sm font-semibold text-neutral-700">
              {differing.length ? `${differing.length} lot${differing.length === 1 ? '' : 's'} differed from the record` : 'Every lot counted matched the record'}
            </h2>
            {differing.length > 0 && (
              <ul className="mt-2 space-y-2">
                {differing.map((l) => (
                  <li key={l.lot_id} className="bg-white border border-neutral-200 rounded-xl p-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-neutral-500 break-words">{sectionTitle(l)} · {placeName(l)}</p>
                      <Link to={`/nursery/lots/${l.lot_id}`} className="font-medium text-primary-700 break-words">{treeName(l)}</Link>
                      <p className="text-xs text-neutral-500 break-words">
                        {l.tree_type_name}{l.lot_code ? ` · ${l.lot_code}` : ''}{l.count?.counted_by_name ? ` · counted by ${l.count.counted_by_name}` : ''}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0 text-sm">
                      <p className="text-neutral-500">{l.count!.shown_count} → {l.count!.counted_count}</p>
                      <p className={cn('font-semibold', l.count!.counted_count < l.count!.shown_count ? 'text-red-700' : 'text-green-700')}>
                        {signed(l.count!.counted_count - l.count!.shown_count)}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {[...queue.values()].some((q) => q.refused) && (
              <p className="mt-3 flex items-start gap-1.5 text-sm text-red-700">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span className="break-words">Some counts on this phone arrived after it was finished and were not saved.</span>
              </p>
            )}
          </section>
        )}
      </div>
      {scanning && <BarcodeScanner label="Scan a lot label" onScan={onScan} onClose={() => setScanning(false)} />}
    </>
  );
}

export default StocktakeCount;
